/**
 * HeaderMenu Component
 *
 * An organism component that displays a dropdown menu for header navigation.
 * Typically used in application headers with a three-dot (ellipsis) trigger button.
 *
 * @module @ideoz/ui/components/organisms/HeaderMenu
 */

import React from 'react';
import { EllipsisVertical } from 'lucide-react';
import { Button } from '../atoms/button';
import type { HeaderMenuProps, MenuItem } from '../../types/HeaderMenu.types';

/**
 * HeaderMenu Component
 *
 * A dropdown menu for header navigation with configurable menu items.
 * Supports icons, disabled states, destructive variants, separators, and submenus.
 */
export const HeaderMenu: React.FC<HeaderMenuProps> = ({
  menuItems,
  triggerLabel = 'Menu',
  triggerIcon: TriggerIcon = EllipsisVertical,
  disabled = false,
  align = 'start',
  className = '',
  testId = 'header-menu',
}) => {
  const [open, setOpen] = React.useState(false);
  const [activeSubmenu, setActiveSubmenu] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
        setActiveSubmenu(null);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleItemClick = (item: MenuItem) => {
    if (item.submenu) {
      setActiveSubmenu(activeSubmenu === item.id ? null : item.id);
    } else {
      item.onClick?.();
      setOpen(false);
      setActiveSubmenu(null);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        size="auto"
        className="w-6 h-6 border border-blue-light-50 hover:bg-blue-light-50 hover:text-blue-light-600 hover:border-blue-light-200 focus:bg-blue-light-50 focus:text-blue-light-600"
        disabled={disabled}
        data-testid={`${testId}-trigger`}
        aria-label={triggerLabel}
        onClick={() => {
          setOpen(!open);
          setActiveSubmenu(null);
        }}
      >
        <TriggerIcon size={14} strokeWidth={2} aria-hidden="true" />
      </Button>

      {open && (
        <div
          className={`absolute ${align === 'end' ? 'right-0' : 'left-0'} mt-2 border p-3 shadow-md w-72 rounded-2xl bg-white z-50 ${className}`}
          data-testid={`${testId}-content`}
        >
          {menuItems.map((item) => {
            const ItemIcon = item.icon;
            const hasSubmenu = !!item.submenu;
            const isSubmenuOpen = activeSubmenu === item.id;
            // Items with submenus should be greyed out only if disabled
            const isDisabledWithSubmenu = item.disabled && hasSubmenu;

            return (
              <div key={item.id} className="relative">
                <button
                  type="button"
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled && !hasSubmenu}
                  data-testid={`${testId}-item-${item.id}`}
                  className={`p-2 w-full text-start cursor-pointer text-sm rounded-md flex gap-2 items-start ${
                    isDisabledWithSubmenu
                      ? 'text-gray-400 hover:bg-blue-light-50'
                      : 'text-primary-600 hover:bg-blue-light-50 hover:text-blue-light-600'
                  }`}
                >
                  {ItemIcon && (
                    <div className="shrink-0">
                      <ItemIcon size={20} strokeWidth={1.5} color="currentColor" aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <span className="flex-1">{item.label}</span>
                    {item.description && (
                      <span className={`text-xs ${isDisabledWithSubmenu ? 'text-gray-400' : 'text-gray-500'}`}>
                        {item.description}
                      </span>
                    )}
                    {item.badge && (
                      <span className="px-2 py-0.5 text-[12px] font-medium rounded-full border border-[#ABEFC6] w-full truncate overflow-hidden text-ellipsis whitespace-nowrap">
                        {item.badge}
                      </span>
                    )}
                  </div>
                </button>

                {/* Submenu */}
                {hasSubmenu && isSubmenuOpen && (
                  <div className="absolute left-full top-0 ml-6 w-72 p-4 rounded-2xl border shadow-md bg-white z-50">
                    {/* Arrow pointing left */}
                    <div className="absolute -left-2 top-4 w-4 h-4 bg-white border-l border-b border-gray-200 rotate-45" />
                    <div className="flex flex-col gap-2 relative">
                      <div>
                        <h4 className="font-medium text-sm text-primary-600">{item.submenu?.title}</h4>
                        {item.submenu?.subtitle && (
                          <span className="text-xs text-gray-500">{item.submenu.subtitle}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{item.submenu?.description}</p>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full mt-2 bg-white text-primary-600 hover:bg-gray-100"
                        onClick={() => {
                          item.submenu?.onAction();
                          setOpen(false);
                          setActiveSubmenu(null);
                        }}
                      >
                        {item.submenu?.actionLabel}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

HeaderMenu.displayName = 'HeaderMenu';
